import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { X, Heart } from "lucide-react";
import type {
  PopupDeckContent,
  PopupDeckCard,
} from "@/shared/types/popup-types";
import type { ResolvedStepDesign } from "@/shared/types/popup-design";
import { fontStack } from "./engine";

export type DeckAnswer = "liked" | "disliked";

interface DeckStageProps {
  deck: PopupDeckContent;
  /** The welcome step's resolved design — the deck wears the same panel skin
   *  (font, text colours) so gate → deck feels like one surface. */
  step: ResolvedStepDesign;
  /** Light panel = dark chrome (dots, pill); dark panel = light chrome. */
  panelIsLight: boolean;
  /** Fires exactly once, when the last card is answered. */
  onComplete: (answers: Record<string, DeckAnswer>) => void;
  /** Optional per-swipe hook (analytics only — persistence is onComplete). */
  onSwipe?: (card: PopupDeckCard, dir: DeckAnswer) => void;
  /** Editor static preview: renders the stack but never advances/completes. */
  inert?: boolean;
}

/** Horizontal distance (px) past which releasing the card commits the swipe.
 *  Below it the card springs back. */
const COMMIT_PX = 96;

/** The idle hint loop (4s): swing left with the dislike badge, settle, swing
 *  right with the like badge, settle, rest — the SVYP teaching animation.
 *  Pure CSS so it costs no timers and stops crisply when the class drops. */
const HINT_CSS = `
@keyframes fp-deck-hint-card {
  0%   { transform: translate3d(0,0,0) rotate(0deg); }
  13%  { transform: translate3d(-76px,6px,0) rotate(-8deg); }
  26%  { transform: translate3d(0,0,0) rotate(0deg); }
  39%  { transform: translate3d(76px,6px,0) rotate(8deg); }
  52%  { transform: translate3d(0,0,0) rotate(0deg); }
  100% { transform: translate3d(0,0,0) rotate(0deg); }
}
@keyframes fp-deck-hint-nope {
  0% { opacity: 0; } 13% { opacity: 0.95; } 26% { opacity: 0; } 100% { opacity: 0; }
}
@keyframes fp-deck-hint-like {
  26% { opacity: 0; } 39% { opacity: 0.95; } 52% { opacity: 0; }
  0% { opacity: 0; } 100% { opacity: 0; }
}
.fp-deck-hint-card { animation: fp-deck-hint-card 4s ease-in-out 0.8s infinite; }
.fp-deck-hint-nope { animation: fp-deck-hint-nope 4s ease-in-out 0.8s infinite; }
.fp-deck-hint-like { animation: fp-deck-hint-like 4s ease-in-out 0.8s infinite; }
`;
/** Fly-out animation length. */
const FLY_MS = 280;

const reducedMotion = (): boolean =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/** The swipe-card game step. A stack of photo cards, each carrying a
 *  first-person statement; swipe right = like, left = dislike. Drag is
 *  hand-rolled on pointer events — no gesture library ships to storefronts.
 *  Cards can also be answered by the ✕/♥ buttons or ←/→ keys, so the deck
 *  is usable without a drag gesture at all. */
export const DeckStage: React.FC<DeckStageProps> = ({
  deck,
  step,
  panelIsLight,
  onComplete,
  onSwipe,
  inert = false,
}) => {
  // Card order is frozen at mount: randomize shuffles ONCE, so progress dots
  // and answer keys stay stable for the whole play.
  const cards = useMemo(() => {
    const list = (deck.cards || []).filter((c) => c && c.image_url && c.key);
    if (!deck.randomize) return list;
    const copy = [...list];
    for (let i = copy.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    return copy;
  }, [deck.cards, deck.randomize]);

  const total = cards.length;
  const [index, setIndex] = useState(0);
  const [drag, setDrag] = useState<{ dx: number; dy: number } | null>(null);
  const [exit, setExit] = useState<DeckAnswer | null>(null);
  // Idle swipe hint: loops until the FIRST interaction of any kind, then
  // never returns — a teaching aid must not fight the hand it taught.
  const [interacted, setInteracted] = useState(false);
  // Deck finished: the last card STAYS flown out (resetting exit would fly
  // it straight back in — the "card comes back" bug) and the completion
  // card underneath holds the stage while the submission round-trips.
  const [done, setDone] = useState(false);
  const answersRef = useRef<Record<string, DeckAnswer>>({});
  const startRef = useRef<{ x: number; y: number } | null>(null);
  const busyRef = useRef(false);

  const likeColor = deck.like_color || "#22c55e";
  const dislikeColor = deck.dislike_color || "#ef4444";
  const showButtons = deck.show_buttons !== false;
  const hintActive =
    deck.hint === true &&
    !interacted &&
    !inert &&
    !drag &&
    exit === null &&
    !reducedMotion();
  const remaining = done ? 0 : Math.max(total - index, 0);
  const progressText = (deck.progress_label || "{n} cards left").replace(
    "{n}",
    String(remaining)
  );

  const commit = useCallback(
    (dir: DeckAnswer) => {
      if (inert || done || busyRef.current || index >= total) return;
      busyRef.current = true;
      setInteracted(true);
      const card = cards[index];
      answersRef.current = { ...answersRef.current, [card.key]: dir };
      onSwipe?.(card, dir);

      const isLast = index + 1 >= total;

      const advance = () => {
        setDrag(null);
        setExit(null);
        startRef.current = null;
        setIndex(index + 1);
        busyRef.current = false;
      };

      // Last card: do NOT reset exit — that would animate the card straight
      // back to centre (there is no next card to replace it). It stays flown
      // out, the completion card underneath takes the stage, and the deck
      // goes inert while onComplete's submission runs.
      const finish = () => {
        setDrag(null);
        startRef.current = null;
        setDone(true);
        onComplete(answersRef.current);
      };

      if (reducedMotion()) {
        if (isLast) finish();
        else advance();
        return;
      }
      setExit(dir);
      window.setTimeout(isLast ? finish : advance, FLY_MS);
    },
    [cards, index, inert, done, onComplete, onSwipe, total]
  );

  // ---- pointer drag (top card only) ----
  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (inert || busyRef.current) return;
      // Links/buttons on the card (product chip) keep their own behaviour.
      if ((e.target as HTMLElement).closest("a,button")) return;
      setInteracted(true);
      startRef.current = { x: e.clientX, y: e.clientY };
      setDrag({ dx: 0, dy: 0 });
      try {
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      } catch {
        /* pointer may already be gone — capture is best-effort */
      }
    },
    [inert]
  );

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (!startRef.current) return;
    setDrag({
      dx: e.clientX - startRef.current.x,
      dy: e.clientY - startRef.current.y,
    });
  }, []);

  const onPointerEnd = useCallback(() => {
    if (!startRef.current) return;
    const dx = drag?.dx ?? 0;
    startRef.current = null;
    if (Math.abs(dx) >= COMMIT_PX) commit(dx > 0 ? "liked" : "disliked");
    else setDrag(null); // spring back
  }, [drag, commit]);

  // ---- keyboard: ← dislike, → like ----
  useEffect(() => {
    if (inert) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") commit("liked");
      else if (e.key === "ArrowLeft") commit("disliked");
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [commit, inert]);

  if (total === 0) return null;

  const headingColor = step.elements.heading.color || "#ffffff";
  const pillBg = panelIsLight ? "rgba(0,0,0,0.07)" : "rgba(255,255,255,0.14)";
  const pillFg = panelIsLight ? "#111827" : "#f3f4f6";

  const dx = drag?.dx ?? 0;
  const likeOpacity = Math.min(Math.max(dx / COMMIT_PX, 0), 1);
  const dislikeOpacity = Math.min(Math.max(-dx / COMMIT_PX, 0), 1);

  // Only the top 3 cards mount (depth illusion without 10 img decodes of DOM
  // weight); painted back-to-front so the top card wins stacking naturally.
  const visibleCards = cards.slice(index, index + 3);

  const verdictBadge = (
    color: string,
    opacity: number,
    icon: React.ReactNode
  ) => (
    <div
      aria-hidden="true"
      style={{
        position: "absolute",
        left: "50%",
        top: "50%",
        width: 64,
        height: 64,
        marginLeft: -32,
        marginTop: -32,
        borderRadius: "50%",
        background: color,
        color: "#ffffff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        opacity,
        transition: drag ? "none" : "opacity 150ms ease",
        pointerEvents: "none",
      }}
    >
      {icon}
    </div>
  );

  return (
    <div
      className="flowapt-deck"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: "100%",
        gap: "clamp(14px, 2.2vh, 22px)",
        padding: "clamp(22px, 4vh, 40px) clamp(16px, 3vw, 28px)",
        fontFamily: fontStack(step.theme.fontFamily),
      }}
    >
      {deck.hint === true && <style>{HINT_CSS}</style>}
      {deck.heading && (
        <p
          style={{
            margin: 0,
            color: headingColor,
            fontWeight: 600,
            fontSize: "clamp(14px, 2vh, 18px)",
            lineHeight: 1.35,
            textAlign: "center",
            maxWidth: "36ch",
          }}
        >
          {deck.heading}
        </p>
      )}

      {/* Card arena */}
      <div
        style={{
          position: "relative",
          width: `min(76vw, ${deck.card_width || 300}px)`,
          height: `min(58vh, ${deck.card_height || 400}px)`,
          touchAction: "pan-y",
        }}
      >
        {/* Terminal card at the BOTTOM of the stack (the SVYP pattern):
            revealed when the last card flies away, it holds the stage while
            the submission round-trips — no empty hole, no snapped-back card. */}
        <div
          aria-hidden={!done}
          style={{
            position: "absolute",
            inset: 0,
            borderRadius: 20,
            background: deck.completion_bg || "#f4f1e8",
            border: "1px solid rgba(0,0,0,0.15)",
            boxShadow: "0 12px 34px -12px rgba(0,0,0,0.55)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "24px",
            zIndex: 0,
          }}
        >
          <p
            style={{
              margin: 0,
              color: deck.completion_text_color || "#111827",
              fontWeight: 600,
              fontSize: "clamp(15px, 2.2vh, 19px)",
              lineHeight: 1.35,
              textAlign: "center",
            }}
          >
            {deck.completion_text || "Thank you for swiping!"}
          </p>
        </div>
        {visibleCards
          .map((card, vi) => {
            const isTop = vi === 0;
            const flying = isTop && exit !== null;
            const tx = flying ? (exit === "liked" ? 640 : -640) : isTop ? dx : 0;
            const ty = isTop && drag ? drag.dy * 0.15 : vi * -10;
            const rot = isTop ? tx * 0.05 : 0;
            const scale = isTop ? 1 : 1 - vi * 0.04;
            return (
              <div
                key={card.key}
                className={isTop && hintActive ? "fp-deck-hint-card" : undefined}
                onPointerDown={isTop ? onPointerDown : undefined}
                onPointerMove={isTop ? onPointerMove : undefined}
                onPointerUp={isTop ? onPointerEnd : undefined}
                onPointerCancel={isTop ? onPointerEnd : undefined}
                style={{
                  position: "absolute",
                  inset: 0,
                  overflow: "hidden",
                  borderRadius: 20,
                  background: "#000",
                  border: "1px solid rgba(0,0,0,0.15)",
                  boxShadow: "0 12px 34px -12px rgba(0,0,0,0.55)",
                  zIndex: 10 - vi,
                  cursor: isTop && !inert ? "grab" : "default",
                  transform: `translate3d(${tx}px, ${ty}px, 0) rotate(${rot}deg) scale(${scale})`,
                  opacity: flying ? 0 : 1,
                  transition:
                    isTop && drag && !flying
                      ? "none"
                      : `transform ${FLY_MS}ms cubic-bezier(0.22,0.61,0.36,1), opacity ${FLY_MS}ms ease`,
                  willChange: "transform",
                  userSelect: "none",
                  WebkitUserSelect: "none",
                }}
              >
                <picture>
                  {card.mobile_image_url && (
                    <source
                      media="(max-width: 767px)"
                      srcSet={card.mobile_image_url}
                    />
                  )}
                  <img
                    src={card.image_url}
                    alt={card.alt || card.statement || "Card"}
                    draggable={false}
                    style={{
                      position: "absolute",
                      inset: 0,
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                </picture>

                {/* Statement over a bottom scrim */}
                {card.statement && (
                  <div
                    style={{
                      position: "absolute",
                      left: 0,
                      right: 0,
                      bottom: 0,
                      padding: "38px 16px 16px",
                      background:
                        "linear-gradient(transparent, rgba(0,0,0,0.86))",
                      color: "#ffffff",
                      fontWeight: 600,
                      fontSize: "clamp(13px, 1.9vh, 16px)",
                      lineHeight: 1.32,
                    }}
                  >
                    {card.statement}
                  </div>
                )}

                {/* Optional product chip */}
                {card.product_url && (
                  <a
                    href={card.product_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onPointerDown={(e) => e.stopPropagation()}
                    style={{
                      position: "absolute",
                      right: 12,
                      top: 12,
                      padding: "5px 12px",
                      borderRadius: 20,
                      background: "rgba(255,255,255,0.92)",
                      color: "#111827",
                      fontSize: 11,
                      fontWeight: 600,
                      textDecoration: "none",
                    }}
                  >
                    {deck.view_product_label || "View product"}
                  </a>
                )}

                {isTop && !hintActive && (
                  <>
                    {verdictBadge(
                      likeColor,
                      exit === "liked" ? 1 : likeOpacity,
                      <Heart style={{ width: 28, height: 28 }} fill="currentColor" />
                    )}
                    {verdictBadge(
                      dislikeColor,
                      exit === "disliked" ? 1 : dislikeOpacity,
                      <X style={{ width: 28, height: 28 }} strokeWidth={3} />
                    )}
                  </>
                )}
                {isTop && hintActive && (
                  <>
                    <div className="fp-deck-hint-like" style={{ opacity: 0 }}>
                      {verdictBadge(
                        likeColor,
                        1,
                        <Heart style={{ width: 28, height: 28 }} fill="currentColor" />
                      )}
                    </div>
                    <div className="fp-deck-hint-nope" style={{ opacity: 0 }}>
                      {verdictBadge(
                        dislikeColor,
                        1,
                        <X style={{ width: 28, height: 28 }} strokeWidth={3} />
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })
          .reverse()}
      </div>

      {/* Tap fallback: ✕ / ♥ answer the top card without a drag. Hidden when
          show_buttons=false (the SVYP look) — drag + arrow keys still work. */}
      {showButtons && (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "clamp(22px, 5vw, 40px)",
        }}
      >
        <button
          type="button"
          aria-label="Dislike"
          disabled={inert}
          onClick={() => commit("disliked")}
          style={{
            width: 52,
            height: 52,
            borderRadius: "50%",
            border: `2px solid ${dislikeColor}`,
            background: panelIsLight ? "#ffffff" : "rgba(255,255,255,0.08)",
            color: dislikeColor,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: inert ? "default" : "pointer",
          }}
        >
          <X style={{ width: 24, height: 24 }} strokeWidth={3} />
        </button>
        <button
          type="button"
          aria-label="Like"
          disabled={inert}
          onClick={() => commit("liked")}
          style={{
            width: 52,
            height: 52,
            borderRadius: "50%",
            border: `2px solid ${likeColor}`,
            background: panelIsLight ? "#ffffff" : "rgba(255,255,255,0.08)",
            color: likeColor,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: inert ? "default" : "pointer",
          }}
        >
          <Heart style={{ width: 24, height: 24 }} fill="currentColor" />
        </button>
      </div>
      )}

      {/* Progress: dots + remaining pill */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 10,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
          {cards.map((c, i) => (
            <span
              key={c.key}
              aria-hidden="true"
              style={{
                width: i === index ? 14 : 5,
                height: 5,
                borderRadius: 3,
                background:
                  i < index || done
                    ? likeColor
                    : i === index
                      ? panelIsLight
                        ? "#111827"
                        : "#ffffff"
                      : panelIsLight
                        ? "rgba(0,0,0,0.18)"
                        : "rgba(255,255,255,0.25)",
                transition: "width 200ms ease",
              }}
            />
          ))}
        </div>
        <span
          style={{
            fontSize: 12,
            fontWeight: 600,
            padding: "4px 12px",
            borderRadius: 20,
            background: pillBg,
            color: pillFg,
          }}
        >
          {progressText}
        </span>
      </div>
    </div>
  );
};

export default DeckStage;
